import React, { useCallback } from 'react';
import { UseMultiSchoolSelect } from 'types';
import Carousel from 'components/sections/carousel/Carousel';
import { SelectionCardAlt } from 'components/form-wizards/micro-portal/SelectionCard/SelectionCardAlt';
import { NOP } from 'utils/generalUtils';

interface Props {
  state: UseMultiSchoolSelect['state'];
  handleSyncCarouselIndex: (index: number) => void;
}

export default function MultiSchoolSelectList({
  state,
  handleSyncCarouselIndex,
}: Props): React.JSX.Element {
  // Find if any school is currently expanded
  const foundIndex = Object.values(state).findIndex(
    (school) => school.isExpanded
  );

  // Handle carousel index changes (from swipes, navigation, etc.)
  const handleCarouselIndexChange = useCallback(
    (newIndex: number) => {
      handleSyncCarouselIndex(newIndex);
    },
    [handleSyncCarouselIndex]
  );

  const schools = Object.values(state);
  if (schools.length === 0) {
    return (
      <div className="multiSchoolSelectList multiSchoolSelectList--noResults" />
    );
  }

  // Carousel must always have a valid index (>= 0). Default to 0 when no card is expanded
  // (e.g. state hydrated from draft, or all isExpanded false).
  let expandedIndex = foundIndex >= 0 ? foundIndex : 0;

  // Ensure expandedIndex is always valid (0 or higher, within bounds)
  // This prevents the carousel from receiving -1 as currentIndex
  // This can happen when clicking a card that isn't selected/skipped,
  // causing EXPAND_CARD to minimize all cards without expanding any
  if (expandedIndex < 0 || expandedIndex >= schools.length) {
    expandedIndex = 0;
  }

  return (
    <div className="multiSchoolSelectList">
      <Carousel
        currentIndex={expandedIndex}
        showNav={false}
        variant="coverFlow"
        scrollToActiveSlide={false}
        // Keep carousel purely controlled by state; avoid resync on animation
        // start (which can cause re-expansion glitches, especially in Safari).
        onSlideAnimationStart={NOP}
        onCarouselIndexChangeAttempt={({ newIndex }) =>
          handleCarouselIndexChange(newIndex)
        }
      >
        {Object.values(state).map((school, index) => (
          <SelectionCardAlt
            key={school.schoolInfo.id}
            id={school.schoolInfo.id}
            index={index}
          />
        ))}
      </Carousel>
    </div>
  );
}
